import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { useState } from "react";
import { useAuth } from "../context/authContext";
import useApi from "../hooks/useApi";
import { updateFollowStatusInCache } from "../utils/cacheUtils";

// Icon-only on mobile (fullscreen), as a 28px square
const ICON_ONLY_SX = {
  minWidth: 0,
  padding: 0,
  height: 28,
  width: 28,
  "& .MuiButton-startIcon": { m: 0, p: 1 },
};

/**
 * Follow / Following toggle for a person in a list (user search, a
 * profile's followers/following). Filled as a call to action while not
 * following, outlined once following (quieter when repeated down a list), so
 * MUI's background/colour transition animates the change, while the + and ✓
 * cross over in place (one shrinks as the other pops).
 */
export default function FollowButton({
  following,
  username,
  iconOnly,
  onClick,
}) {
  const reduceMotion = useReducedMotion();
  return (
    <Button
      // Outlined in both states, filled when not following: switching to the
      // contained variant would change the padding and nudge the size
      variant="outlined"
      size="small"
      aria-pressed={following}
      aria-label={
        iconOnly
          ? `${following ? "Unfollow" : "Follow"} @${username}`
          : undefined
      }
      onClick={onClick}
      sx={[
        !following && {
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "&:hover": { bgcolor: "primary.dark" },
        },
        iconOnly && ICON_ONLY_SX,
      ]}
      startIcon={
        // Both icons always present in a fixed 18px slot, crossing over at
        // once: the slot never resizes, so the button doesn't either
        <Box
          component="span"
          sx={{ position: "relative", display: "flex", width: 18, height: 18 }}
        >
          {[
            {
              key: "follow",
              icon: <AddIcon fontSize="inherit" />,
              shown: !following,
            },
            {
              key: "following",
              icon: <CheckIcon fontSize="inherit" />,
              shown: following,
            },
          ].map(({ key, icon, shown }) => (
            <Box
              key={key}
              component={motion.span}
              // No animation on first render, only on a change
              initial={false}
              animate={
                shown
                  ? { scale: 1, rotate: 0, opacity: 1 }
                  : {
                      scale: 0,
                      rotate: -90,
                      opacity: 0,
                    }
              }
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 520, damping: 28 }
              }
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          ))}
        </Box>
      }
    >
      {/* Both labels share one grid cell, only the current one visible, so
          the button is always as wide as "Following" and never jumps */}
      {!iconOnly && (
        <Box component="span" sx={{ display: "inline-grid" }}>
          {["Following", "Follow"].map((label) => (
            <Box
              key={label}
              component="span"
              sx={{
                gridArea: "1 / 1",
                textAlign: "center",
                visibility:
                  (label === "Following") === following ? "visible" : "hidden",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      )}
    </Button>
  );
}

/**
 * Follow/unfollow for a list of people (search results, a profile's network),
 * each `{ username, isFollowing }`. Optimistic: isFollowing(person) flips the
 * moment toggleFollow(person) is called and flips back if the request fails;
 * it overrides the person's own isFollowing, which is only as fresh as the
 * data it came with. Taps during a request for that person are ignored.
 */
export function useFollowToggle() {
  const { followUser, unfollowUser } = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const followMutation = useMutation({
    mutationFn: (person) => followUser(person.username),
    onSuccess: (_, person) =>
      updateFollowStatusInCache({
        queryClient,
        currentUser: user,
        username: person.username,
        followStatus: true,
      }),
  });

  const unfollowMutation = useMutation({
    mutationFn: (person) => unfollowUser(person.username),
    onSuccess: (_, person) =>
      updateFollowStatusInCache({
        queryClient,
        currentUser: user,
        username: person.username,
        followStatus: false,
      }),
  });

  // Optimistic follow state by username (see the hook's docs)
  const [followOverrides, setFollowOverrides] = useState({});
  const isFollowing = (person) =>
    followOverrides[person.username] ?? person.isFollowing;
  const isPendingFor = (person) =>
    [followMutation, unfollowMutation].some(
      (mutation) =>
        mutation.isPending && mutation.variables?.username === person.username,
    );

  // Taps during a request are ignored rather than disabling the button, which
  // would grey out the fill just as it animates in
  const toggleFollow = (person) => {
    if (isPendingFor(person)) return;
    const next = !isFollowing(person);
    const setFollowing = (value) =>
      setFollowOverrides((overrides) => ({
        ...overrides,
        [person.username]: value,
      }));
    setFollowing(next);
    (next ? followMutation : unfollowMutation).mutate(person, {
      onError: () => setFollowing(!next),
    });
  };

  return { isFollowing, toggleFollow };
}
